import React, { useState, useEffect } from 'react';
import MedicationList from './admin/MedicationList';
import UserManagement from './admin/UserManagement';
import Analytics from './admin/Analytics';
import Queue from './admin/Queue';
import BulkDeleteModal from './admin/BulkDeleteModal';
import { admin } from '../services/api';
import '../styles/AdminPanel.css';

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('pipeline');
  const [actionLoading, setActionLoading] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [medicationCount, setMedicationCount] = useState(0);

  const tabs = [
    { id: 'pipeline', label: '🔄 Pipeline', component: MedicationList },
    { id: 'review-queue', label: '⚡ Simplifying', component: Queue },
    { id: 'analytics', label: '📊 Analytics', component: Analytics },
    { id: 'queue', label: '📋 Import Queue', component: Queue },
    { id: 'medications', label: '💊 All Medications', component: MedicationList },
    { id: 'users', label: '👥 Users', component: UserManagement },
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component || Analytics;
  const showDeleteAllAction = activeTab === 'pipeline' || activeTab === 'medications';

  useEffect(() => {
    const fetchMedicationCount = async () => {
      if (!showDeleteAllAction) return;
      try {
        const response = await admin.getMedications({ sort_by: 'date_desc' });
        setMedicationCount(Array.isArray(response.data) ? response.data.length : 0);
      } catch (err) {
        console.error('Failed to fetch medication count:', err);
        setMedicationCount(0);
      }
    };

    fetchMedicationCount();
  }, [activeTab, showDeleteAllAction]);

  const handleExportData = async () => {
    try {
      setActionLoading('export');
      const response = await admin.exportData();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `simplemeds-export-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert(err.response?.data?.detail || 'Export failed. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkRelease = async () => {
    if (!window.confirm('Release all stuck claims (claimed >24h)? This will make those medications available again.')) {
      return;
    }
    try {
      setActionLoading('release');
      const response = await admin.bulkReleaseStuck();
      alert(`Released ${response.data.count} stuck medications.`);
    } catch (err) {
      console.error('Bulk release failed:', err);
      alert(err.response?.data?.detail || 'Bulk release failed.');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>Admin Dashboard</h2>
        <p className="admin-subtitle">Manage pharmacists, medications, and view analytics</p>
      </div>

      <div className="admin-tabs">
        {tabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="admin-content">
        <ActiveComponent />
      </div>

      <div className="admin-quick-actions">
        <h3>Quick Actions</h3>
        <div className="quick-actions-buttons">
          <button
            className="btn-action"
            onClick={handleExportData}
            disabled={actionLoading === 'export'}
          >
            {actionLoading === 'export' ? 'Exporting...' : '📥 Export All Data (CSV)'}
          </button>
          <button
            className="btn-action btn-warning"
            onClick={handleBulkRelease}
            disabled={actionLoading === 'release'}
          >
            {actionLoading === 'release' ? 'Releasing...' : '🔓 Bulk Release Stuck Claims'}
          </button>
          {showDeleteAllAction && (
            <button
              className="btn-action btn-danger"
              onClick={() => setShowDeleteAllModal(true)}
            >
              🗑️ Delete All Medications
            </button>
          )}
        </div>
      </div>

      {showDeleteAllModal && (
        <BulkDeleteModal
          show={showDeleteAllModal}
          selectedIds={[]}
          deleteAll={true}
          totalCount={medicationCount}
          onClose={() => setShowDeleteAllModal(false)}
          onDeleteComplete={() => {
            setShowDeleteAllModal(false);
            setMedicationCount(0);
          }}
        />
      )}
    </div>
  );
}

export default AdminPanel;
